import React, { useEffect } from "react";
import Layout from "../components/ui/Layout";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import { motion, AnimatePresence } from "framer-motion";

import QuoteHero from "../components/QuoteHero";
import ContentHero from "../components/ContentHero";

import Slide1 from "../assets/img/slide-1.png";
import Slide2 from "../assets/img/slide-2.png";
import Slide3 from "../assets/img/slide-3.png";
import Slide4 from "../assets/img/slide-4.jpg";

// Animazione per il titolo principale in sovrimpressione
const titleVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.8, 
      ease: "easeOut"
    }
  }
};

// Animazione per il sottotitolo
const subtitleVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.8, 
      delay: 0.3, 
      ease: "easeOut"
    }
  }
};

// Animazione per il pulsante CTA
const buttonVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { 
      duration: 0.5, 
      delay: 0.6,
      ease: "easeOut"
    }
  },
  hover: {
    scale: 1.05,
    backgroundColor: "#305570",
    transition: { duration: 0.2 }
  }
};

export default function HomePage() {
  useEffect(() => {
    // Cookie consent code remains unchanged
    document.documentElement.classList.add('cc--darkmode');
    
    CookieConsent.run({
      guiOptions: {
        consentModal: {
          layout: "box wide",
          position: "bottom left",
          equalWeightButtons: false,
          flipButtons: true,
        },
        preferencesModal: {
          layout: "box",
          position: "right",
          equalWeightButtons: true,
          flipButtons: false,
        },
      },
      categories: {
        necessary: {
          readOnly: true,
        },
        functionality: {},
        analytics: {},
      },
      language: {
        default: "it",
        autoDetect: "browser",
        translations: {
          en: {
            consentModal: {
              title: "This website uses cookies",
              description:
                "We use cookies to ensure you get the best experience on our website. Please review our Privacy Policy for more information and/or to change your cookie settings at any time. If you continue to use this site, you consent to our use of cookies.",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              showPreferencesBtn: "Manage preferences",
              footer:
                '<a href="/privacy-policy">Privacy Policy</a>\n<a href="/terms-of-service">Terms and conditions</a>',
            },
            preferencesModal: {
              title: "Consent Preferences Center",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Save preferences",
              closeIconLabel: "Close modal",
              serviceCounterLabel: "Service|Services",
              sections: [
                {
                  title: "Cookie Usage",
                  description:
                    "We use cookies to ensure you get the best experience on our website. Please review our Privacy Policy for more information and/or to change your cookie settings at any time.",
                },
                {
                  title:
                    'Strictly Necessary Cookies <span class="pm__badge">Always Enabled</span>',
                  description:
                    "These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Functionality Cookies",
                  description:
                    "These cookies enable the website to provide enhanced functionality and personalisation. They may be set by us or by third party providers whose services we have added to our pages.",
                  linkedCategory: "functionality",
                },
                {
                  title: "Analytics Cookies",
                  description:
                    "These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular and see how visitors move around the site.",
                  linkedCategory: "analytics",
                },
                {
                  title: "More information",
                  description:
                    'For any query in relation to my policy on cookies and your choices, please <a class="cc__link" href="/contact">contact us</a>.',
                },
              ],
            },
          },
          it: {
            consentModal: {
              title: "Questo sito web utilizza i cookie",
              description:
                "Utilizziamo i cookie per garantire che tu possa avere la migliore esperienza sul nostro sito web. Per ulteriori informazioni e/o per modificare le tue impostazioni sui cookie in qualsiasi momento, consulta la nostra Informativa sulla privacy. Se continui a utilizzare questo sito, acconsenti al nostro utilizzo dei cookie.",
              acceptAllBtn: "Accetta tutto",
              acceptNecessaryBtn: "Rifiuta tutto",
              showPreferencesBtn: "Gestisci preferenze",
              footer:
                '<a href="/privacy-policy">Informativa sulla privacy</a>\n<a href="/terms-of-service">Termini e condizioni</a>',
            },
            preferencesModal: {
              title: "Centro preferenze per il consenso",
              acceptAllBtn: "Accetta tutto",
              acceptNecessaryBtn: "Rifiuta tutto",
              savePreferencesBtn: "Salva le preferenze",
              closeIconLabel: "Chiudi la finestra",
              serviceCounterLabel: "Servizi",
              sections: [
                {
                  title: "Utilizzo dei Cookie",
                  description:
                    "Utilizziamo i cookie per garantire che tu possa avere la migliore esperienza sul nostro sito web. Per ulteriori informazioni e/o per modificare le tue impostazioni sui cookie in qualsiasi momento, consulta la nostra Informativa sulla privacy.",
                },
                {
                  title:
                    'Cookie Strettamente Necessari <span class="pm__badge">Sempre Attivati</span>',
                  description:
                    "Questi cookie sono necessari per il funzionamento del sito web e non possono essere disattivati nei nostri sistemi. Di solito vengono impostati solo in risposta ad azioni da te effettuate che costituiscono una richiesta di servizi, come l'impostazione delle preferenze sulla privacy, l'accesso o la compilazione di moduli.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Cookie di Funzionalità",
                  description:
                    "Questi cookie consentono al sito web di offrire funzionalità e personalizzazioni avanzate. Possono essere impostati da noi o da fornitori terzi i cui servizi abbiamo aggiunto alle nostre pagine.",
                  linkedCategory: "functionality",
                },
                {
                  title: "Cookie Analitici",
                  description:
                    "Questi cookie ci consentono di contare le visite e le fonti di traffico in modo da poter misurare e migliorare le prestazioni del nostro sito. Ci aiutano a sapere quali pagine sono le più e le meno popolari e a vedere come si muovono i visitatori nel sito.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Ulteriori informazioni",
                  description:
                    'Per qualsiasi domanda in relazione alla mia politica sui cookie e alle tue scelte, ti preghiamo di <a class="cc__link" href="/contact">contattarci</a>.',
                },
              ],
            },
          },
        },
      },
    });
  }, []);

  return (
    <>
      <section className="relative bg-[#212121] h-[80vh] mt-18 overflow-hidden">
        <meta name="description" content="Ferro Marrocco - Artisti del Ferro" />
        <ImageSlider />
        
        {/* Overlay testo animato */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-black/30 px-6 text-center">
          <motion.h1 
            className="text-4xl md:text-6xl font-bold text-white mb-6"
            initial="hidden"
            animate="visible"
            variants={titleVariants}
          >
            Marrocco ART
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl text-white/90 max-w-2xl"
            initial="hidden"
            animate="visible"
            variants={subtitleVariants}
          >
            Trasformiamo il ferro in opere d'arte, unendo tradizione e innovazione da oltre 40 anni
          </motion.p>
          <motion.a 
            href="/store"
            className="mt-8 px-8 py-3 bg-[#406580] text-white rounded-lg text-lg font-medium"
            initial="hidden"
            animate="visible"
            whileHover="hover"
            variants={buttonVariants}
          >
            Scopri le Nostre Creazioni
          </motion.a>
        </div>
      </section>
      
      <motion.section 
        className="w-full bg-white z-10"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <Layout>
          <QuoteHero />
          <ContentHero />
        </Layout>
      </motion.section>
    </>
  );
}

// Componente per animare i contatori numerici
const CounterBox = ({ number, text, plus = false }) => {
  return (
    <motion.div 
      className="bg-white/10 backdrop-blur-sm p-8 rounded-xl text-center"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      whileHover={{ y: -5, transition: { duration: 0.3 } }}
      viewport={{ once: true }}
    >
      <motion.span 
        className="text-4xl md:text-5xl font-bold block"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        viewport={{ once: true }}
      >
        {number}{plus && "+"}
      </motion.span>
      <motion.p 
        className="text-lg mt-2 text-white/80"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        viewport={{ once: true }}
      >
        {text}
      </motion.p>
    </motion.div>
  );
};

const ImageSlider = () => {
  const [currentImage, setCurrentImage] = React.useState(0);
  const [direction, setDirection] = React.useState(1);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const slides = [Slide1, Slide2, Slide3, Slide4];

  useEffect(() => {
    // Auto-advance slides with longer interval (10s)
    const interval = setInterval(() => {
      if (!isAnimating) {
        setDirection(1);
        setCurrentImage(prevImage => (prevImage + 1) % slides.length);
      }
    }, 10000); // 10 seconds per slide for better user experience

    return () => clearInterval(interval);
  }, [slides.length, isAnimating]);

  const goToSlide = (index) => {
    if (isAnimating || index === currentImage) return;
    setDirection(index > currentImage ? 1 : -1);
    setCurrentImage(index);
  };

  return (
    <div className="relative w-full h-full overflow-hidden bg-[#212121]">
      <AnimatePresence 
        initial={false} 
        custom={direction}
        onExitComplete={() => setIsAnimating(false)}
      >
        <motion.div
          key={currentImage}
          custom={direction}
          initial={(direction) => ({
            x: direction * 100 + "%",
            opacity: 0.8,
          })}
          animate={{ 
            x: 0,
            opacity: 1,
          }}
          exit={(direction) => ({ 
            x: direction * -100 + "%",
            opacity: 0.8,
          })}
          transition={{ 
            x: { type: "tween", duration: 1, ease: "easeInOut" },
            opacity: { duration: 0.8 }
          }}
          className="absolute inset-0"
          onAnimationStart={() => setIsAnimating(true)}
        >
          {/* Preload di tutte le immagini */}
          {slides.map((slide, index) => (
            <link 
              key={index} 
              rel="preload" 
              as="image" 
              href={slide} 
            />
          ))}
          <img 
            src={slides[currentImage]} 
            alt={`Slide ${currentImage + 1}`}
            className="w-full h-full object-cover scale-[1.02]" // leggero zoom per effetto di movimento
            style={{ 
              transform: "translateZ(0)",
              animation: "subtle-zoom 10s ease-in-out infinite alternate" 
            }}
            loading="eager"
          />
          {/* Overlay per migliorare la leggibilità del testo */}
          <div className="absolute inset-0 bg-black opacity-30" />
        </motion.div>
      </AnimatePresence>
      
      {/* Indicatori migliorati */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-10 bg-black/40 px-3 py-2 rounded-full">
        {slides.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              currentImage === index 
                ? "bg-white w-8" 
                : "bg-white/40 w-2.5 hover:bg-white/60"
            }`}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};